/**
 * assetService.js
 * ---------------
 * Asset management service that connects to backend API
 * NO DIRECT FIREBASE ACCESS - All operations go through backend
 */

import { createLogger } from '../utils/logger';

const logger = createLogger('AssetService');
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class AssetService {
  
  /**
   * Normalize app name to create appPrefix (lowercase, no spaces)
   */
  normalizeAppName(appId) {
    return appId.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /**
   * Upload asset via backend API
   * @param {File} file - File to upload
   * @param {string} appId - Application ID
   * @param {object} metadata - Additional metadata (description, uploadedBy)
   * @param {function} onProgress - Progress callback (optional)
   * @returns {Promise<object>} - Uploaded asset data with id and downloadURL
   */
  async uploadAsset(file, appId, metadata = {}, onProgress = null) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File size exceeds 50MB limit');
      }

      logger.info('uploadAsset', 'Starting upload via backend', { 
        fileName: file.name, 
        size: file.size, 
        appId
      });

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('appId', appId);
      formData.append('description', metadata.description || '');
      formData.append('uploadedBy', metadata.uploadedBy || 'user');

      // Upload via backend API
      const response = await fetch(`${API_BASE_URL}/assets/upload`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for multipart
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      const assetData = result.data || result;

      logger.info('uploadAsset', 'Upload successful', { assetId: assetData.id });
      return assetData;
    } catch (error) {
      logger.error('uploadAsset', 'Upload error', error);
      throw error;
    }
  }

  /**
   * List all assets for an app via backend API
   * @param {string} appId - Application ID
   * @returns {Promise<Array>} - Array of assets
   */
  async listAssets(appId) {
    try {
      if (!appId) {
        throw new Error('appId is required');
      }

      logger.info('listAssets', 'Fetching assets via backend', { appId });

      const response = await fetch(`${API_BASE_URL}/assets/${appId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch assets: ${response.status}`);
      }

      const result = await response.json();
      const assets = result.data?.assets || result.assets || [];

      logger.info('listAssets', 'Assets fetched', { count: assets.length });
      return assets;
    } catch (error) {
      logger.error('listAssets', 'Failed to fetch assets', error);
      throw error;
    }
  }

  /**
   * Get single asset by ID via backend API
   * @param {string} appId - Application ID
   * @param {string} assetId - Asset ID
   * @returns {Promise<object>} - Asset data
   */
  async getAsset(appId, assetId) {
    try {
      logger.info('getAsset', 'Fetching asset via backend', { appId, assetId });

      const response = await fetch(`${API_BASE_URL}/assets/${appId}/${assetId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch asset: ${response.status}`);
      }

      const result = await response.json();
      const asset = result.data || result;

      return asset;
    } catch (error) {
      logger.error('getAsset', 'Failed to fetch asset', error);
      throw error;
    }
  }

  /**
   * Delete an asset via backend API
   * @param {string} appId - Application ID
   * @param {string} assetId - Asset ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteAsset(appId, assetId) {
    try {
      logger.info('deleteAsset', 'Deleting asset via backend', { appId, assetId });

      const response = await fetch(`${API_BASE_URL}/assets/${appId}/${assetId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete asset: ${response.status}`);
      }

      logger.info('deleteAsset', 'Asset deleted successfully');
      return true;
    } catch (error) {
      logger.error('deleteAsset', 'Failed to delete asset', error);
      throw error;
    }
  }

  /**
   * Update asset metadata via backend API
   * @param {string} appId - Application ID
   * @param {string} assetId - Asset ID
   * @param {object} updates - Metadata updates (name, description, etc.)
   * @returns {Promise<object>} - Updated asset data
   */
  async updateAssetMetadata(appId, assetId, updates) {
    try {
      logger.info('updateAssetMetadata', 'Updating metadata via backend', { appId, assetId });

      const response = await fetch(`${API_BASE_URL}/assets/${appId}/${assetId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update metadata: ${response.status}`);
      }

      const result = await response.json();
      const asset = result.data || result;

      logger.info('updateAssetMetadata', 'Metadata updated successfully');
      return asset;
    } catch (error) {
      logger.error('updateAssetMetadata', 'Failed to update metadata', error);
      throw error;
    }
  }

  /**
   * Get download URL for an asset (returns existing downloadURL)
   * @param {string} path - Storage path
   * @returns {Promise<string>} - Download URL
   */
  async getSignedUrl(path) {
    try {
      logger.info('getSignedUrl', 'Getting download URL from Storage', { path });

      const storageRef = ref(storage, path);
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      logger.error('getSignedUrl', 'Failed to get download URL', error);
      throw new Error(`Failed to get download URL: ${error.message}`);
    }
  }

  /**
   * Search assets by name or description
   * @param {string} appId - Application ID
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} - Filtered assets
   */
  async searchAssets(appId, searchTerm) {
    try {
      const assets = await this.listAssets(appId);
      
      if (!searchTerm) {
        return assets;
      }

      const lowerSearch = searchTerm.toLowerCase();
      const filtered = assets.filter(asset =>
        asset.name.toLowerCase().includes(lowerSearch) ||
        (asset.description && asset.description.toLowerCase().includes(lowerSearch))
      );

      logger.info('searchAssets', 'Search completed', { term: searchTerm, results: filtered.length });
      return filtered;
    } catch (error) {
      logger.error('searchAssets', 'Search failed', error);
      throw error;
    }
  }

  /**
   * Get file type category for icon display
   * @param {string} fileType - MIME type
   * @returns {string} - Category (image, video, audio, document, other)
   */
  getFileCategory(fileType) {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.startsWith('video/')) return 'video';
    if (fileType.startsWith('audio/')) return 'audio';
    if (fileType.includes('pdf') || fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('csv')) {
      return 'document';
    }
    return 'other';
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted size string
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

// Export singleton instance
const assetService = new AssetService();
export default assetService;
